import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import apiClient from '../utils/api'; 
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel } from 'swiper/modules';
import WishesCard from '../components/WishesCard'; 
import { useAuth } from '../contexts/AuthContext';
import { locationData } from '../utils/constants';
import Select from 'react-select';

import 'swiper/css';
import './WishesPage.css';

const WishesPage = ({ setActiveCommentPostId }) => {
    const { currentUser } = useAuth();
    const [wishesContent, setWishesContent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasMore, setHasMore] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);

    const isFetching = useRef(false); 
    const lastTimestampRef = useRef(null);
    const hasMoreRef = useRef(true);

    const [filters, setFilters] = useState({ 
        postType: 'Special Day Wishes', 
        state: '', 
        district: '', 
        constituency: '', 
        mandal: '' 
    });

    const setHasMoreStatus = (status) => {
        setHasMore(status);
        hasMoreRef.current = status;
    };

    const stateOptions = useMemo(() => locationData.map(s => ({ value: s.state_en, label: s.state_te })), []);
    
    const districtOptions = useMemo(() => {
        if (!filters.state) return [];
        const stateData = locationData.find(s => s.state_en === filters.state);
        return stateData ? stateData.districts.map(d => ({ value: d.district_en, label: d.district_te })) : [];
    }, [filters.state]);

    const constituencyOptions = useMemo(() => {
        if (!filters.state || !filters.district) return []; 
        const stateData = locationData.find(s => s.state_en === filters.state);
        const districtData = stateData?.districts.find(d => d.district_en === filters.district);
        return districtData?.constituencies 
            ? districtData.constituencies.map(c => ({ value: String(c.value), label: String(c.label) })) 
            : []; 
    }, [filters.state, filters.district]);

    const mandalOptions = useMemo(() => {
        if (!filters.state || !filters.district) return []; 
        const stateData = locationData.find(s => s.state_en === filters.state);
        const districtData = stateData?.districts.find(d => d.district_en === filters.district);
        if (!districtData?.mandals) return [];
        return districtData.mandals.map(m => {
            if (typeof m === 'object' && m !== null) {
                return { value: String(m.value || m.mandal_en || ''), label: String(m.label || m.mandal_te || '') };
            }
            return { value: String(m), label: String(m) };
        });
    }, [filters.state, filters.district]);

    // Premium horizontal select pills matching category style
    const pillSelectStyles = {
        control: (provided, state) => {
            const hasValue = state.hasValue;
            return {
                ...provided,
                backgroundColor: hasValue ? 'transparent' : 'rgba(255,255,255,0.06)',
                backgroundImage: hasValue ? 'linear-gradient(135deg, #ff9800, #ff5722)' : 'none',
                border: hasValue ? 'none' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: '24px',
                minHeight: '34px',
                height: '34px',
                padding: '0 8px 0 12px',
                boxShadow: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                transition: 'all 0.25s ease',
                '&:hover': {
                    backgroundColor: hasValue ? 'transparent' : 'rgba(255,255,255,0.12)',
                    borderColor: hasValue ? 'transparent' : 'rgba(255,255,255,0.3)',
                }
            };
        },
        valueContainer: (provided) => ({
            ...provided,
            padding: '0',
            margin: '0',
            display: 'flex',
            alignItems: 'center',
            height: '34px',
            overflow: 'visible',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#ffffff',
            fontSize: '13.5px',
            fontWeight: '700',
            margin: '0',
            padding: '0',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: 'rgba(255,255,255,0.65)',
            fontSize: '13.5px',
            fontWeight: '700',
            margin: '0',
            padding: '0',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '34px',
            padding: '0',
        }),
        indicatorSeparator: () => ({
            display: 'none'
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            padding: '0 2px',
            color: state.hasValue ? '#ffffff' : 'rgba(255,255,255,0.4)',
            '&:hover': { color: '#ffffff' }
        }),
        clearIndicator: (provided, state) => ({
            ...provided,
            padding: '0 2px',
            color: state.hasValue ? '#ffffff' : 'rgba(255,255,255,0.4)',
            '&:hover': { color: '#ffffff' }
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#1e1e1e',
            borderRadius: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255,255,255,0.1)',
            overflow: 'hidden',
            zIndex: 9999,
            width: '200px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected 
                ? '#ff9800' 
                : state.isFocused 
                    ? 'rgba(255,255,255,0.08)' 
                    : 'transparent',
            color: '#ffffff',
            fontSize: '13.5px',
            fontWeight: '600',
            padding: '8px 12px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            '&:active': { backgroundColor: '#ff5722' }
        })
    };

    const fetchWishes = useCallback(async (currentFilters, isLoadMore = false) => {
        if (isFetching.current || (isLoadMore && !hasMoreRef.current)) return;

        isFetching.current = true;
        if (!isLoadMore) {
            setLoading(true);
            lastTimestampRef.current = null;
        }
        setError('');
        
        try {
            const params = { limit: 20 };
            const { postType, state, district, constituency, mandal } = currentFilters;
            
            if (isLoadMore && lastTimestampRef.current) {
                params.lastTimestamp = lastTimestampRef.current;
            }
            
            const matchedState = locationData.find(s => s.state_en === state);
            const stateTe = matchedState ? matchedState.state_te : '';
            
            const matchedDistrict = matchedState?.districts.find(d => d.district_en === district);
            const districtTe = matchedDistrict ? matchedDistrict.district_te : '';

            if (state) {
                params.state = state;
                params.stateEn = state;
                params.stateTe = stateTe;
            }
            if (district) {
                params.district = district;
                params.districtEn = district;
                params.districtTe = districtTe;
            }
            if (constituency) params.constituency = constituency;
            if (mandal) params.mandal = mandal;

            params.postType = postType || 'Special Day Wishes';

            const response = await apiClient.get('/user-news', { params });
            const rawWishes = response.data;

            if (rawWishes.length < 20) {
                setHasMoreStatus(false);
            } else {
                setHasMoreStatus(true);
            }

            if (rawWishes.length > 0) {
                lastTimestampRef.current = rawWishes[rawWishes.length - 1].timestamp;
                const processedItems = rawWishes.map(item => ({
                    ...item,
                    timestamp: item.timestamp?._seconds 
                        ? new Date(item.timestamp._seconds * 1000) 
                        : new Date(item.timestamp || Date.now())
                }));

                setWishesContent(prev => {
                    return isLoadMore ? [...prev, ...processedItems] : processedItems;
                });
            } else if (!isLoadMore) {
                setWishesContent([]);
                setHasMoreStatus(false);
            }
        } catch (err) {
            console.error("Error fetching wishes:", err);
            setError('శుభాకాంక్షలను లోడ్ చేయడంలో విఫలమైంది.');
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    }, []);

    useEffect(() => {
        fetchWishes(filters);
    }, [filters, fetchWishes]);

    return (
        <div className="wishes-page-focused">
            
            {/* Styled Filter Controls with Horizontal dark-mode pill design */}
            <div 
                className="wishes-filter-ui"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px 16px',
                    backgroundColor: '#121212',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    height: '52px',
                    flexShrink: 0,
                    boxSizing: 'border-box'
                }}
            >
                <Select
                    options={stateOptions}
                    value={stateOptions.find(s => s.value === filters.state) || null}
                    onChange={selected => setFilters(prev => ({ ...prev, state: selected ? selected.value : '', district: '', constituency: '', mandal: '' }))}
                    placeholder="రాష్ట్రం (State)" isClearable
                    menuPortalTarget={document.body} 
                    styles={pillSelectStyles}
                />

                {filters.state && (
                    <Select
                        options={districtOptions}
                        value={districtOptions.find(d => d.value === filters.district) || null}
                        onChange={selected => setFilters(prev => ({ ...prev, district: selected ? selected.value : '', constituency: '', mandal: '' }))}
                        placeholder="జిల్ల్లా (District)" isClearable
                        menuPortalTarget={document.body} 
                        styles={pillSelectStyles}
                    />
                )}

                {filters.district && (
                    <Select
                        options={constituencyOptions}
                        value={constituencyOptions.find(c => c.value === filters.constituency) || null}
                        onChange={selected => setFilters(prev => ({ ...prev, constituency: selected ? selected.value : '', mandal: '' }))}
                        placeholder="निओजाकवार्गम (Constituency)" isClearable
                        menuPortalTarget={document.body} 
                        styles={pillSelectStyles}
                    />
                )}

                {filters.constituency && (
                    <Select
                        options={mandalOptions}
                        value={mandalOptions.find(m => m.value === filters.mandal) || null}
                        onChange={selected => setFilters(prev => ({ ...prev, mandal: selected ? selected.value : '' }))}
                        placeholder="మండలం" isClearable
                        menuPortalTarget={document.body} 
                        styles={pillSelectStyles}
                    />
                )}
            </div>

            {/* Vertically Swipeable Wishes Viewport */}
            <div className="wishes-viewport">
                {loading && wishesContent.length === 0 ? (
                    <div className="no-wishes-found">
                        <p>లోడ్ అవుతోంది...</p>
                    </div>
                ) : !loading && wishesContent.length === 0 ? (
                    <div className="no-wishes-found">
                        <h3>శుభాకాంక్షలు ఏవీ లేవు</h3>
                        <p>ఈ ప్రాంతంలో ప్రస్తుతం ఎటువంటి శుభాకాంక్షలు అందుబాటులో లేవు.</p>
                    </div>
                ) : (
                    <Swiper
                        direction={'vertical'}
                        slidesPerView={1}
                        mousewheel={true}
                        modules={[Mousewheel]}
                        className="wishes-vertical-swiper"
                        style={{ height: '100%' }} 
                        onSlideChange={(swiper) => {
                            setActiveIndex(swiper.activeIndex);
                            const threshold = wishesContent.length - 5;
                            if (swiper.activeIndex >= threshold && hasMoreRef.current && !isFetching.current) {
                                fetchWishes(filters, true);
                            }
                        }}
                    >
                        {wishesContent.map((item, index) => (
                            <SwiperSlide 
                                key={item.id || `wish-slide-${index}`} 
                                style={{ height: '100%' }}
                            >
                                <div style={{ width: '100%', height: '100%' }}>
                                    <WishesCard 
                                        newsItem={{ ...item, description: item.content || item.description }} 
                                        isActive={index === activeIndex} 
                                        setActiveCommentPostId={setActiveCommentPostId} 
                                    />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
            </div>
        </div>
    );
};

export default WishesPage;